import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Table,
  Button,
  Input,
  FormGroup,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { Line, Bar } from "react-chartjs-2";
import CardsHeader from "components/Headers/CardsHeader.js";
import SearchableDropdown from "components/DropDown/SearchableDropdown";
import { PRODUCT_TYPE_OPTIONS } from "variables/constants";
import { DATE_RANGE_OPTIONS } from "variables/constants";
import moment from "moment";
import ResetPassword from "components/Modal/ResetPassword";
import NotificationAlert from "react-notification-alert";
import { resetPassword } from "actions/users";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getAdminChartData } from "actions/dashboard";
import { getAdminKpiData } from "actions/dashboard";
import { getAdminPerformanceData } from "actions/dashboard";
import ReactBsTable from "components/Tables/ReactBsTable";
import { getProductTypeList } from "actions/productType";
import { getPublisherKpiData } from "actions/publisher";
import { formatValues } from "components/Common/commonFunctions";
import BankDetails from "components/Modal/BankDetails";
import { createPublisherBillingInfo } from "actions/publisher";
import BankApprovalPendingModal from "components/Modal/BlockingModal";
import { publisherBillingStatus } from "actions/publisher";

const sortFieldMap = {
  date: "Date",
  rawRequests: "RawRequests", 
  rawImpressions: "RawImpressions",
  netRevenue: "NetRevenue",
  publisherPayout: "PublisherPayout", 
  pnl: "Pnl",
  pnlPercentage: "PnlPercentage",
};

const formatterMap = {
  rawRequests: (v) => v?.toLocaleString?.(),
  rawImpressions: (v) => v?.toLocaleString?.(),

  netRevenue: (v) => <strong>${v}</strong>,
  publisherPayout: (v) => <strong>${v}</strong>,

  pnl: (value) => {
    const numValue = parseFloat(value) || 0;
    return (
      <span
        className={numValue > 0 ? "text-success font-weight-bold" : "text-danger font-weight-bold"}
        style={{ display: "block", textAlign: "right" }}
      >
        {numValue >= 0 ? `$${numValue.toFixed(2)}` : `-$${Math.abs(numValue).toFixed(2)}`}
      </span>
    );
  },
  pnlPercentage: (v) => <Badge color={v > 0 ? "success" : "danger"}>{v}%</Badge>,
};

const generateColumns = (data = [], sortField = "", sortOrder = "") => {
  if (!data.length) return [];

  const rightAlignKeys = [
    "rawRequests",
    "rawImpressions",
    "netRevenue",
    "publisherPayout",
    "pnl",
    "pnlPercentage",
  ];

  const sortCaret = (order, column) => {
    const isActive = sortField === column.dataField;
    
    if (!order && !isActive) {
      return (
        <span className="sort-caret" style={{ marginLeft: "8px" }}>
          <i className="fa fa-sort" />
        </span>
      );
    }
    if ((order === "asc" && isActive) || (sortOrder === "asc" && isActive)) {
      return (
        <span className="sort-caret" style={{ marginLeft: "8px" }}>
          <i className="fa fa-sort-up" />
        </span>
      );
    }
    if ((order === "desc" && isActive) || (sortOrder === "desc" && isActive)) {
      return (
        <span className="sort-caret" style={{ marginLeft: "8px" }}>
          <i className="fa fa-sort-down" />
        </span>
      );
    }
    return (
      <span className="sort-caret" style={{ marginLeft: "8px" }}>
        <i className="fa fa-sort" />
      </span>
    );
  };

  return Object.keys(data[0]).map((key) => ({
    text: key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase()),
    dataField: key,
    formatter: formatterMap[key] ?? ((cell) => cell),
    sort: true,
    sortCaret: (order) => sortCaret(order, { dataField: key }),
    align: rightAlignKeys.includes(key) ? "right" : "left",
    headerAlign: rightAlignKeys.includes(key) ? "right" : "left",
  }));
};

const AdminDailyPNDashboard = () => {
  // State management
  const [dateRange, setDateRange] = useState("MTD");
  const [reportType, setReportType] = useState("Raw");
  const [viewBy, setViewBy] = useState("Date");
  const [pageNum, setPageNum] = useState(1);
  const [showLoader, setShowLoader] = useState(false);

  const [chartData, setchartData] = useState({
    data: [],
  });
  const [kpiData, setkpiData] = useState({
    adminValues: {
      netRevenue: "",
      publisherPayout: "",
      pnl: "",
      pnlPercent: "",
    },
    publisherValues: {
      adRequests: "",
      impression: "",
      fillRate: "",
      erpm: "",
      revenue: "",
    },
  });

  const [performanceData, setPerformanceData] = useState({
    data: [],
    columns: [],
    totalRecords: 0,
    totalCounts: null,
  });
  
  // Add sort config state
  const [sortConfig, setSortConfig] = useState({
    dataField: "",
    sortField: "",
    sortOrder: "",
  });
  const [filterModal, setFilterModal] = useState(false);
  const [columnsModal, setColumnsModal] = useState(false);
  const [pageSize, setpageSize] = useState(50);
  const visibleColumnsSnapshot = useRef({});
  const [dropDownData, setDropDownData] = useState({
    productTypeData: [],
  });
  const localStorageData = JSON.parse(localStorage.getItem("p"));
  const [filters, setFilters] = useState({
    deal_search: "",
    product_type: "",
    date_range: "",
    start_date: "",
    end_date: "",
    filterParams: [],
  });

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    date: true,
    rawRequests: true,
    rawImpressions: true,
    netRevenue: true,
    publisherPayout: true,
    pnl: true,
    pnlPercentage: true,
  });

  const flattenProcessedData = (data = [], pageNum = 1, totalCounts = null) => {
    return data.map((item, index) => ({
      date: item.date || "-",
      rawRequests: formatValues(item.rawAdRequests) || 0,
      rawImpressions: formatValues(item.rawImpressions) || 0,
      netRevenue: formatValues(item.netRevenue),
      publisherPayout: formatValues(item?.publisherPayout) || 0,
      pnl: item.pnl,
      pnlPercentage: formatValues(item.pnlPercentage),
    }));
  };

  useEffect(() => {
    if (!performanceData?.columns?.length) return;

    const visibilityMap = performanceData.columns.reduce((acc, col) => {
      acc[col.dataField] = true;
      return acc;
    }, {});

    setVisibleColumns(visibilityMap);
  }, [performanceData.columns]);

  const handleSizePerPageChange = (e) => {
    const newSize = Number(e.target.value);
    setpageSize(newSize);
    setPageNum(1);
  };

  const tableColumns = useMemo(() => {
    if (!performanceData.columns.length) return [];
    return performanceData?.columns?.filter(
      (col) => visibleColumns[col.dataField],
    );
  }, [visibleColumns, performanceData]);

  // Table data with total row
  const tableData = useMemo(() => {
    if (!performanceData?.data?.length) return [];

    if (!performanceData?.totalCounts) return performanceData.data;

    const totalCounts = performanceData.totalCounts;
    
    const totalRow = {
      date: "TOTAL",
      rawRequests: formatValues(totalCounts.totalOfRawRequests) || 0,
      rawImpressions: formatValues(totalCounts.totalOfRawImpressions) || 0,
      netRevenue: formatValues(totalCounts.totalOfNetRevenue) || 0,
      publisherPayout: formatValues(totalCounts.totalOfPublisherPayout) || 0,
      pnl: totalCounts.totalOfPnl || 0,
      pnlPercentage: formatValues(totalCounts.totalOfPnlPercentage) || 0,
    };

    return [...performanceData.data, totalRow];
  }, [performanceData]);

  // Call Dashboard Api
  const getDashboardChartData = async () => {
    try {
      if (localStorageData.role === "PUBLISHER" || localStorageData.role === "PUBLISHER_SUB_USER") {
        const [chartDataRes, adminPerformanceRes] = await Promise.all([
          getAdminChartData(filters.filterParams),
          getAdminPerformanceData(pageNum, filters, pageSize, "", sortConfig.sortField, sortConfig.sortOrder),
          getPublisherKPIData()
        ]);

        if (chartDataRes?.code === 200) {
          setchartData({
            data: chartDataRes.data.dashboardAnalytics,
          });
        }

        if (adminPerformanceRes?.code === 200) {
          const tableDataFormatted = flattenProcessedData(
            adminPerformanceRes.data.adminPerformance,
            pageNum,
            adminPerformanceRes.data.totalCounts
          );
          const columns = generateColumns(tableDataFormatted, sortConfig.sortField, sortConfig.sortOrder);
          setPerformanceData((prev) => ({
            data: tableDataFormatted,
            columns: columns,
            totalRecords: adminPerformanceRes?.data.totalRecords,
            totalCounts: adminPerformanceRes?.data.totalCounts || null,
          }));
        }
      } else {
        const [chartDataRes, adminPerformanceRes] = await Promise.all([
          getAdminChartData(filters.filterParams),
          getAdminPerformanceData(pageNum, filters, pageSize, "", sortConfig.sortField, sortConfig.sortOrder),
          getAdminKPIData(),
        ]);

        if (chartDataRes?.code === 200) {
          setchartData({
            data: chartDataRes.data.dashboardAnalytics,
          });
        }

        if (adminPerformanceRes?.code === 200) {
          const tableDataFormatted = flattenProcessedData(
            adminPerformanceRes.data.adminPerformance,
            pageNum,
            adminPerformanceRes.data.totalCounts
          );
          const columns = generateColumns(tableDataFormatted, sortConfig.sortField, sortConfig.sortOrder);
          setPerformanceData((prev) => ({
            data: tableDataFormatted,
            columns: columns,
            totalRecords: adminPerformanceRes?.data.totalRecords,
            totalCounts: adminPerformanceRes?.data.totalCounts || null,
          }));
        }
      }
    } catch (error) { 
      console.error("Error fetching dashboard data:", error);
    }
  };

  const getPublisherKPIData = async () => {
    try {
      const res = await getPublisherKpiData(filters);
      if (res?.code === 200) {
        let calculatedERPM = (res?.data.netRevenue / res?.data.impressions) * 1000;
        let calculatedFillRate = (res?.data.impressions / res?.data.adRequests) * 100;
        setkpiData({
          publisherValues: {
            adRequests: res?.data.adRequests,
            impression: res?.data.impressions,
            fillRate: calculatedFillRate.toFixed(2),
            erpm: calculatedERPM.toFixed(2),
            revenue: res?.data.netRevenue?.toFixed(2),
          },
        });
      }
    } catch (error) { 
      console.error("Error fetching publisher KPI data:", error);
    }
  };

  const getAdminKPIData = async () => {
    try {
      const res = await getAdminKpiData(filters);
      if (res?.code === 200) {
        let data = res?.data;
        setkpiData({
          adminValues: {
            netRevenue: data.netRevenue?.toFixed(2),
            pnl: data.pnl,
            pnlPercent: data.pnlPercentage?.toFixed(2),
            publisherPayout: data.publisherPayout?.toFixed(2),
          },
        });
      }
    } catch (error) { 
      console.error("Error fetching admin KPI data:", error);
    }
  };

  useEffect(() => {
    if (localStorageData?.role === "PUBLISHER" || localStorageData.role === "PUBLISHER_SUB_USER") {
      getPublisherKPIData();
    } else {
      getAdminKPIData();
    }
  }, []);

  useEffect(() => {
    getDashboardChartData();
  }, [filters.filterParams, pageNum, pageSize, sortConfig.sortField, sortConfig.sortOrder]);

  // Handle sort change
  const handleSortChange = (dataField, sortOrder) => {
    // Map the dataField to the correct backend field name
    const mappedSortField = sortFieldMap[dataField] || dataField;
    setSortConfig({ 
      dataField: dataField, 
      sortField: mappedSortField, 
      sortOrder: sortOrder 
    });
    setPageNum(1);
  };

  // ============ BILLING STATUS CHECK ============
  const [billingStatus, setBillingStatus] = useState(null);
  const [isCheckingBilling, setIsCheckingBilling] = useState(true);

  const checkBillingStatus = async () => {
    try {
      setIsCheckingBilling(true);
      const res = await publisherBillingStatus();

      
      if (res?.code === 200 && res?.data) {
        // Handle different possible status field names
        const status = res.data.billingStatus || 
                      res.data.billing_status || 
                      res.data.status || 
                      "";
        

        setBillingStatus(status);
        setRejectionReason(res.data.rejectionReason || res.data.rejection_reason || "");
        // Update localStorage with the latest billing status
        const updatedData = { ...localStorageData, billingStatus: status };
        localStorage.setItem("p", JSON.stringify(updatedData));
        
        return status;
      } else {
        console.error("Failed to fetch billing status:", res?.message);
        setBillingStatus("");
        setRejectionReason("")
        return "";
      }
    } catch (error) {
      console.error("Error checking billing status:", error);
      setBillingStatus("");
       setRejectionReason("")
      return "";
    } finally {
      setIsCheckingBilling(false);
    }
  };

  // Check billing status on every refresh/load
  useEffect(() => {
    // Only check for publisher role
    if (localStorageData?.role === "PUBLISHER" || localStorageData?.role === "PUBLISHER_SUB_USER") {
      checkBillingStatus();
    } else {
      setIsCheckingBilling(false);
    }
  }, []); // Empty dependency array means it runs once on mount

  //==> buildFilterParams function is used for make filterJson array.
  const buildFilterParams = () => {
    const filterParams = [];

    if (filters?.start_date !== "" && filters?.end_date !== "") {
      filterParams.push({
        field: "Date",
        condition: "btw",
        filterValues: [filters?.start_date, filters?.end_date],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  // Chart data
  const revenueLineChart = {
    labels: chartData?.data.map((item) => item.date),
    datasets: [
      {
        label: localStorageData?.role === "PUBLISHER" || localStorageData.role === "PUBLISHER_SUB_USER" ? "Revenue ($)" : "Net Revenue ($)",
        data: chartData?.data.map((item) => item.netRevenue),
        borderColor: "#00acb4",
        backgroundColor: "rgba(0, 171, 180, 0.27)",
        borderWidth: 2,
        fill: true,
        pointRadius: 3,
        pointBackgroundColor: "#00acb4",
        pointBorderColor: "#00acb4",
        tension: 0.4,
      },
    ],
  };

  //===> handleFilterChange function is used for set filters values in a state.
  const handleChange = (key, value) => {
    setFilters((prev) => {
      if (key === "start_date") {
        const startDate = value;
        const endDate = prev.end_date;

        if (endDate && new Date(startDate) <= new Date(endDate)) {
          return {
            ...prev,
            start_date: startDate,
          };
        }

        return {
          ...prev,
          start_date: startDate,
          end_date: "",
        };
      }

      return {
        ...prev,
        [key]: value,
      };
    });
  };

  const requestsBarChart = {
    labels: chartData?.data.map((item) => item.date),
    datasets: [
      {
        label: "Ad Requests",
        data: chartData?.data.map((item) => item.adRequests),
        backgroundColor: "#00acb4",
        borderColor: "#00acb4",
        borderWidth: 1,
      },
      {
        label: "Impressions",
        data: chartData?.data.map((item) => item.impressions),
        backgroundColor: "#2f3438",
        borderColor: "#2f3438",
        borderWidth: 1,
      },
    ],
  };

  // Toggle handlers
  const toggleFilterModal = () => setFilterModal(!filterModal);
  const toggleColumnsModalSave = () => setColumnsModal(!columnsModal);

  const toggleColumnsModal = () => {
    if (!columnsModal) {
      visibleColumnsSnapshot.current = { ...visibleColumns };
    }
    setColumnsModal(!columnsModal);
  };

  const handleCloseModal = () => {
    setVisibleColumns(visibleColumnsSnapshot.current);
    setColumnsModal(false);
  };

  const notificationAlertRef = useRef(null);
  const [resetPasswordModalShow, setresetPasswordModalShow] = useState(false);
  const [isBankDetailsModalShow, setIsBankDetailsModalShow] = useState(false);
  const [blockingModal, setBlockingModal] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
const [rejectionReason, setRejectionReason] = useState("");
  
  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem("p"));
  const isPasswordSet = userData?.isPasswordSet;
  const billingStatusFromStorage = userData?.billingStatus;

  // Effect to handle modal flow - one at a time
// Update the useEffect to handle REJECTED status
useEffect(() => {
  // Use the billing status from state (API response) or from localStorage as fallback
  const currentBillingStatus = billingStatus || billingStatusFromStorage || "";
  
  // Priority 1: Password reset
  if (isPasswordSet === "NO") {

    setresetPasswordModalShow(true);
    setIsBankDetailsModalShow(false);
    setBlockingModal(false);
    setIsRejected(false);
    return;
  }

  // Priority 2: Bank details form for PENDING, REJECTED, or INACTIVE status
  if (currentBillingStatus === "PENDING") {

    setIsRejected(false);
    setRejectionReason("");
    setIsBankDetailsModalShow(true);
    setresetPasswordModalShow(false);
    setBlockingModal(false);
    return;
  }

  // Handle REJECTED status - show bank details with rejection message
  if (currentBillingStatus === "INACTIVE") {

    setIsRejected(true);
    // You can set a custom rejection reason if available
    setRejectionReason("");
    setIsBankDetailsModalShow(true);
    setresetPasswordModalShow(false);
    setBlockingModal(false);
    return;
  }

  // Priority 3: Blocking modal for PENDING_APPROVAL or APPROVAL_PENDING
  if (currentBillingStatus === "PENDING_APPROVAL" || currentBillingStatus === "APPROVAL_PENDING") {
  
    setBlockingModal(true);
    setresetPasswordModalShow(false);
    setIsBankDetailsModalShow(false);
    setIsRejected(false);
    return;
  }

  // If all conditions are satisfied (password set, billing approved)
  if (isPasswordSet === "YES" && currentBillingStatus === "APPROVED") {

    setresetPasswordModalShow(false);
    setIsBankDetailsModalShow(false);
    setBlockingModal(false);
    setIsRejected(false);
  }
}, [isPasswordSet, billingStatus, billingStatusFromStorage]);

  // Column toggle handler
  const handleColumnToggle = (column) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  //==> isAnyFilterApplied function is used for to check is any filter is applied or not to avoid api call.
  const isAnyFilterApplied = (filters) => {
    return Object.entries(filters).some(([key, value]) => {
      if (key === "filterParams") {
        return Array.isArray(value) && value.length > 0;
      }
      return value !== "" && value !== null && value !== undefined;
    });
  };

  const handleSearch = () => {
    if (!isAnyFilterApplied(filters)) return;
    const newfilterParams = buildFilterParams();

    setFilters({
      ...filters,
      filterParams: newfilterParams,
    });
  };

  const handleReset = () => {
    setFilters({
      date_range: "",
      start_date: "",
      end_date: "",
      filterParams: [],
    });
  };

  //==========================Reset Password Api ====
  const resetPasswordApi = async (payload) => {
    const res = await resetPassword(payload);
    if (res?.code === 200) {
      notify("success", "Password reset successfully");
      setresetPasswordModalShow(false);
      const updatedData = { ...localStorageData, isPasswordSet: "YES" };
      localStorage.setItem("p", JSON.stringify(updatedData));
      // Update the userData to trigger the useEffect
      window.location.reload(); // Optional: reload to update all states
    } else {
      notify("danger", res?.message || "Something went wrong");
      setresetPasswordModalShow(true);
    }
  };

  // Get publisher ID from localStorage
  const publisherID = localStorageData?.id || localStorageData?.publisherID;

  // Placeholder functions - implement these based on your actual requirements
  const getBillingInfo = async () => {
    // This should fetch the billing info - implement based on your needs

    // Add your actual implementation here
  };

  const getBankHistory = async () => {
    // This should fetch the bank history - implement based on your needs

    // Add your actual implementation here
  };

  const handleBankSubmit = async (payload) => {

    try {
      setShowLoader(true);
      
      // Prepare the payload matching the Go struct
      const apiPayload = {
        publisherID: publisherID,
        legalName: payload.billingInfo.legalEntityName || payload.billingInfo.legal_entity_name,
        billingAddress: payload.billingInfo.billingAddress || payload.billingInfo.billing_address,
        country: parseInt(payload.billingInfo.country) || 0,
        vatNumber: payload.billingInfo.trnVatId || payload.billingInfo.trn_vat_id || "",
        paymentTerms: payload.billingInfo.paymentTerms || payload.billingInfo.payment_terms || "Net 30",
        minimumPayout: parseFloat(payload.billingInfo.minimumPayout) || 500,
        bankName: payload.billingInfo.bankName || payload.billingInfo.bank_name,
        accountNumber: payload.billingInfo.bankAccount || payload.billingInfo.bank_account,
        iban: payload.billingInfo.iban || "",
        swiftCode: payload.billingInfo.swiftBic || payload.billingInfo.swift_bic,
        ifscCode: payload.billingInfo.ifscCode || payload.billingInfo.ifsc_code || "",
        invoiceEmail: payload.billingInfo.contactEmail || payload.billingInfo.contact_email || payload.billingInfo.invoice_email,
        recipientName: payload.billingInfo.recipientName || payload.billingInfo.recipient_name || payload.billingInfo.legalEntityName,
        recipientAddress: payload.billingInfo.recipientAddress || payload.billingInfo.recipient_address,
        taxResidency: payload.billingInfo.taxResidency || payload.billingInfo.tax_residency || "",
        paymentCurrency: payload.billingInfo.paymentCurrency || payload.billingInfo.payment_currency || "USD",
        routingNumber: payload.billingInfo.routingNumber || payload.billingInfo.routing_number || "",
      };

      const res = await createPublisherBillingInfo(apiPayload);
      
      if (res.code === 200) {
        notify("success", "Bank details saved successfully! Awaiting admin approval.");
        setShowLoader(false);
        await Promise.all([getBillingInfo(), getBankHistory()]);
        
        // Close bank details modal and update localStorage
        setIsBankDetailsModalShow(false);
        const updatedData = { 
          ...localStorageData, 
          billingStatus: "ACTIVE" 
        };
        localStorage.setItem("p", JSON.stringify(updatedData));
        
        // Update the billing status state
        setBillingStatus("PENDING_APPROVAL");
         setRejectionReason("")
        
        // Reload to trigger the modal flow
        window.location.reload();
        
        return { code: 200 };
      } else {
        setShowLoader(false);
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Failed to save bank details";
        notify("danger", message);
        return { code: res.code, message: message };
      }
    } catch (error) {
      setShowLoader(false);
      console.error("Error saving bank details:", error);
      notify("danger", "Something went wrong while saving bank details");
      return { code: 500, message: "Something went wrong" };
    }
  };

  const notify = (type, message) => {
    notificationAlertRef.current.notificationAlert({
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const showCustomDateInputs = (name, value) => {
    const today = moment().format("YYYY-MM-DD");

    if (value === "Custom Range") {
      setFilters((prev) => ({
        ...prev,
        date_range: value,
        start_date: "",
        end_date: "",
      }));
      return;
    }

    let months = 0;
    if (value === "Last 1 Month") months = 1;
    if (value === "Last 3 Month") months = 3;
    if (value === "Last 6 Month") months = 6;
    if (value === "Last 12 Month") months = 12;

    const startDate = moment().subtract(months, "months").format("YYYY-MM-DD");

    setFilters((prev) => ({
      ...prev,
      date_range: value,
      start_date: startDate,
      end_date: today,
    }));
  };

  const getProductTypeData = async () => {
    try {
      const res = await getProductTypeList(
        dropDownData.productTypeData.pageNum,
        `[{"field":"Status","condition":"eq","filterValues":["ACTIVE"]}]`,
        dropDownData.productTypeData.searchString,
      );
      const productTypeData =
        res?.code === 200
          ? (res?.data?.ProductType || [])
            .filter((item) => item?.status === "ACTIVE")
            .map((item) => ({
              label: item?.name || "",
              value: item?.ID,
            }))
          : [];
      setDropDownData((prev) => ({
        ...prev,
        productTypeData: productTypeData,
      }));
    } catch (error) { 
      console.error("Error fetching product types:", error);
    }
  };

  useEffect(() => {
    getProductTypeData();
  }, []);

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      
      {/* Blocking Modal - Highest priority, shows when pending approval */}
    {localStorageData.loginAs !== "PUBLISHER" &&(
      <BankApprovalPendingModal isOpen={blockingModal} />
    )}
      
      <CardsHeader
        name={(localStorageData?.role !== "PUBLISHER" && localStorageData.role !== "PUBLISHER_SUB_USER") ? "Daily P&L Dashboard (Month To Date)" : "Dashboard"}
        parentName={
         (localStorageData?.role !== "PUBLISHER" && localStorageData.role !== "PUBLISHER_SUB_USER") ? "Admin" : "PUBLISHER"
        }
        kpiData={kpiData}
      />

      <Container fluid className="mt--6">
        <Card className="mb-4">
          <CardBody>
            <Row className="mt-3">
              <Col md={2}>
                <SearchableDropdown
                  options={DATE_RANGE_OPTIONS}
                  placeholder="Date Range"
                  value={filters.date_range}
                  onChange={(e) =>
                    showCustomDateInputs("date_range", e.target.value)
                  }
                  isSearchable={false}
                />
              </Col>

              <Col md={2}>
                <div className="datepicker-wrapper">
                  <DatePicker
                    selected={
                      filters.start_date ? new Date(filters.start_date) : null
                    }
                    onChange={(date) =>
                      handleChange("start_date", date ? formatDate(date) : "")
                    }
                    dateFormat="dd MMM yyyy"
                    placeholderText="Start Date"
                    className="form-control custom-datepicker"
                    disabled={filters.date_range !== "Custom Range"}
                  />
                  <i className="ni ni-calendar-grid-58 text-muted calendar-icon" />
                </div>
              </Col>

              <Col md={2}>
                <div className="datepicker-wrapper">
                  <DatePicker
                    selected={
                      filters.end_date ? new Date(filters.end_date) : null
                    }
                    onChange={(date) =>
                      handleChange("end_date", date ? formatDate(date) : "")
                    }
                    dateFormat="dd MMM yyyy"
                    placeholderText="End Date"
                    className="form-control custom-datepicker"
                    disabled={
                      filters.date_range !== "Custom Range" || !filters.start_date
                    }
                    minDate={
                      filters.start_date
                        ? new Date(filters.start_date)
                        : undefined
                    }
                  />
                  <i className="ni ni-calendar-grid-58 text-muted calendar-icon" />
                </div>
              </Col>

              <Col md={4}>
                <Button
                  onClick={handleSearch}
                  className="btn-gradient-primary btn-md responsive"
                  disabled={!isAnyFilterApplied(filters)}
                >
                  Apply
                </Button>

                <Button
                  className="secondary"
                  onClick={handleReset}
                  disabled={!isAnyFilterApplied(filters)}
                >
                  Reset
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Charts Row */}
        <Row>
          <Col xl="6">
            <Card className="mb-4">
              <CardHeader>
                <h5 className="h3 mb-0">{(localStorageData?.role !== "PUBLISHER" && localStorageData.role !== "PUBLISHER_SUB_USER") ? "Daily Revenue Trend" : "Daily Net Revenue Trend"}</h5>
              </CardHeader>
              <CardBody>
                <div style={{ height: "300px" }}>
                  <Line
                    data={revenueLineChart}
                    options={{
                      maintainAspectRatio: false,
                      legend: { display: true, position: "top" },
                      tooltips: {
                        mode: "index",
                      },
                      scales: {
                        xAxes: [
                          {
                            gridLines: {
                              display: true,
                              drawBorder: true,
                              color: "rgba(255, 255, 255, 0.7)",
                              lineWidth: 0,
                            },
                            ticks: { display: true, autoSkip: true, fontColor: "#495057" },
                          },
                        ],
                        yAxes: [
                          {
                            ticks: {
                              drawBorder: true,
                              display: true,
                              callback: function (value) {
                                return Number(value).toLocaleString();
                              },
                            },
                            gridLines: {
                              beginAtZero: true,
                              display: true,
                              drawBorder: true,
                              color: "rgba(0, 0, 0, 0.7)",
                              lineWidth: 0,
                            },
                          },
                        ],
                      },
                    }}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl="6">
            <Card className="mb-4">
              <CardHeader>
                <h5 className="h3 mb-0">Requests vs Impressions</h5>
              </CardHeader>
              <CardBody>
                <div style={{ height: "300px" }}>
                  <Bar
                    data={requestsBarChart}
                    options={{
                      maintainAspectRatio: false,
                      legend: { display: true, position: "top" },
                      tooltips: {
                        mode: "index",
                        intersect: false,
                        callbacks: {
                          label: function (tooltipItem, data) {
                            const label = data.datasets[tooltipItem.datasetIndex].label || "";
                            const value = tooltipItem.yLabel;
                            const formatted = Number(value).toLocaleString();
                            return label ? `${label}: ${formatted}` : formatted;
                          },
                        },
                      },
                      scales: {
                        xAxes: [
                          {
                            gridLines: {
                              display: true,
                              drawBorder: true,
                              color: "rgba(0, 0, 0, 0.7)",
                              lineWidth: 0,
                            },
                            ticks: { display: true, autoSkip: true, fontColor: "#495057" },
                          },
                        ],
                        yAxes: [
                          {
                            ticks: {
                              display: true,
                              beginAtZero: true,
                              callback: function (value) {
                                return Number(value).toLocaleString();
                              },
                              fontColor: "#000000",
                            },
                            gridLines: {
                              beginAtZero: true,
                              display: true,
                              drawBorder: true,
                              color: "rgba(255, 255, 255, 0.7)",
                              lineWidth: 0,
                            },
                          },
                        ],
                      },
                    }}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {localStorageData?.role !== "PUBLISHER" && localStorageData.role !== "PUBLISHER_SUB_USER" && (
          <Card>
            {tableColumns?.length > 0 && (
              <ReactBsTable
                title="Admin Performance Report"
                data={tableData}
                columns={tableColumns}
                keyField="date"
                showAddbtn={false}
                showColumnsBtn={true}
                Columnstext="Columns"
                toggleColumnsModal={toggleColumnsModal}
                handleSizePerPageChange={handleSizePerPageChange}
                page={pageNum}
                enablePageSizeManagement={true}
                sizePerPage={pageSize}
                totalRecords={performanceData?.totalRecords}
                onPageChange={(p) => setPageNum(p)}
                onSortChange={handleSortChange}
                defaultSortField={sortConfig.dataField}
                defaultSortOrder={sortConfig.sortOrder}
                showAdminPerformanceHeading={true}
              />
            )}
          </Card>
        )}

        {/* Modals */}
        {/* Columns Modal */}
        <Modal isOpen={columnsModal} toggle={toggleColumnsModalSave}>
          <ModalHeader toggle={toggleColumnsModalSave}>
            Select Columns to Display
          </ModalHeader>
          <ModalBody>
            {Object.entries(visibleColumns).map(([key, value]) => (
              <label
                key={key}
                className="custom-checkbox-icon d-flex align-items-center mb-3"
              >
                <input
                  type="checkbox"
                  checked={value}
                  onChange={() => handleColumnToggle(key)}
                  hidden
                />

                <span className="checkbox-icon mr-2">
                  {value && <i className="fa fa-check" />}
                </span>

                {key.charAt(0).toUpperCase() +
                  key.slice(1).replace(/([A-Z])/g, " $1")}
              </label>
            ))}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={handleCloseModal}>
              Close
            </Button>
            <Button
              className="btn-gradient-primary"
              onClick={toggleColumnsModal}
            >
              Save Selection
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
      
      {/* Reset Password Modal */}
      <ResetPassword
        isOpen={resetPasswordModalShow}
        toggleModal={() => {}}
        onSubmit={(data) => resetPasswordApi(data)}
      />
      
      {/* Bank Details Modal */}
       {localStorageData.loginAs !== "PUBLISHER" &&(

<BankDetails
  isOpen={isBankDetailsModalShow}
  toggleModal={() => {}}
  onSubmit={(data) => handleBankSubmit(data)}
  isRejected={isRejected}
  rejectionReason={rejectionReason}
/>
       )}
    </>
  );
};

export default AdminDailyPNDashboard;